import { FlaskConical, Tag } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Card, Flow, StatTile, useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { useLabActions, useLabDashboard } from '@/features/orders/api';
import { isApiError } from '@/lib/api';
import { LabelsDialog } from '@/pages/lab/LabelsDialog';
import { PatientIdentityText } from '@/pages/patients/PatientIdentityText';
import styles from './dashboards.module.css';

const time = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/**
 * Lab dashboard (artifact #112): To collect today, At the lab, Results in,
 * Home visits; the sample pipeline Booked → Collected → At the lab → To
 * review → Released; and the collection list.
 */
export function LabDashboard() {
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const { data } = useLabDashboard(!!branch);
  const a = useLabActions();
  const toast = useToast();
  const [labels, setLabels] = useState<string | null>(null);
  if (!branch)
    return (
      <p className={styles.warnNote}>
        Choose the branch you are working at in the branch switch.
      </p>
    );
  const t = data?.tiles;
  const p = data?.pipeline;
  return (
    <div className={styles.stack}>
      <div className={styles.tiles}>
        <StatTile
          highlight
          label="To collect today"
          value={t?.toCollectToday ?? '—'}
        />
        <StatTile label="At the lab" value={t?.atTheLab ?? '—'} />
        <StatTile
          label="Results in"
          value={t?.resultsInToday ?? '—'}
          caption={
            t?.unmatchedResults ? (
              <Link to="/lab/results">{`${t.unmatchedResults} held for matching`}</Link>
            ) : (
              'Today'
            )
          }
        />
        <StatTile label="Home visits" value={t?.homeVisits ?? '—'} />
      </div>
      <Card title="Sample pipeline" flush>
        <div aria-label="Sample pipeline">
          <Flow
            steps={(
              [
                ['Booked', p?.booked],
                ['Collected', p?.collected],
                ['At the lab', p?.atTheLab],
                ['To review', p?.toReview],
                ['Released today', p?.releasedToday],
              ] as const
            ).map(([label, n]) => ({
              label,
              value: n ?? '—',
              hot: label === 'To review' && !!n,
              zero: !n,
            }))}
          />
        </div>
      </Card>
      <Card title="Collection list">
        {!data?.collectionList.length ? (
          <p className={styles.muted}>Nothing to collect.</p>
        ) : (
          <table className={styles.table}>
            <tbody>
              {data.collectionList.map((o) => (
                <tr key={o.id}>
                  <td>
                    <strong>{o.patient.fullName}</strong>
                    <br />
                    <span className={styles.muted}>
                      <PatientIdentityText identity={o.patient.identity} />
                    </span>
                  </td>
                  <td>
                    {o.tests.map((x) => x.testName).join(', ')}
                    <br />
                    <span className={styles.muted}>
                      {[
                        ...new Set(
                          o.tests.map((x) => x.tube ?? 'Plain / SST (gold)'),
                        ),
                      ].join(', ')}
                    </span>
                  </td>
                  <td>
                    {o.collectionMode === 'home' ? (
                      <Badge tone="info">{`Home ${time(o.homeVisitAt)}`}</Badge>
                    ) : (
                      <Badge>{`At the clinic · ${time(o.orderedAt)}`}</Badge>
                    )}
                  </td>
                  <td>
                    {hasPermission('lab_orders:process') && (
                      <span className={styles.badges}>
                        <Button
                          size="sm"
                          icon={<Tag size={14} />}
                          onClick={() => setLabels(o.id)}
                        >
                          Labels
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          icon={<FlaskConical size={14} />}
                          onClick={() =>
                            void a.collect
                              .mutateAsync(o.id)
                              .catch((err: unknown) =>
                                toast.show({
                                  tone: 'error',
                                  title: 'Not done',
                                  body: isApiError(err)
                                    ? err.message
                                    : undefined,
                                  error: err,
                                }),
                              )
                          }
                        >
                          Sample collected
                        </Button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {labels && (
        <LabelsDialog orderId={labels} onClose={() => setLabels(null)} />
      )}
    </div>
  );
}
